import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import ImageIcon from "@saleor/icons/Image";
import { commonMessages } from "@saleor/intl";
import classNames from "classnames";
import React from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";

import { AnnouncementFormData } from "../AnnouncementCreatePage";

interface AnnouncementImageProps {
  data: AnnouncementFormData;
  placeholderImage?: string;
  loading?: boolean;
  onChange: FormChange;
  onImageUpload(file: File);
}

const AnnouncementImage: React.FC<AnnouncementImageProps> = props => {
  const { data, loading, onChange, onImageUpload } = props;
  const notify = useNotifier();

  const useStyles = makeStyles(
    theme => ({
      card: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
          marginTop: 0
        }
      },
      descriptionLabel: {
        margin: "5px 0px 5px 0px"
      },
      fileField: {
        display: "none"
      },
      fileUploadField: {
        height: "400px",
        textAlign: "center"
      },
      helpText: {
        gridColumnEnd: "span 4"
      },
      icon: {
        color: "rgba(255, 255, 255, 0.54)"
      },
      image: {
        height: "100%",
        objectFit: "contain",
        userSelect: "none",
        width: "100%"
      },
      imageContainer: {
        "&:hover, &.dragged": {
          "& $imageOverlay": {
            display: "block"
          }
        },
        background: "#ffffff",
        border: "1px solid #eaeaea",
        borderRadius: theme.spacing(),
        height: "100%",
        margin: "auto",
        overflow: "hidden",
        // padding: theme.spacing(2),
        position: "relative",
        width: "100%"
      },
      imageGridContainer: {
        position: "relative"
      },
      imageOverlay: {
        background: "rgba(0, 0, 0, 0.6)",
        cursor: "move",
        display: "none",
        height: "100%",
        left: 0,
        // padding: theme.spacing(2),
        position: "absolute",
        top: 0,
        width: "100%"
      },
      imageOverlayShow: {
        "&$imageOverlay": {
          alignItems: "center",
          display: "flex",
          justifyContent: "center"
        }
      },
      imageOverlayToolbar: {
        alignContent: "flex-end",
        display: "flex",
        left: "95%",
        position: "relative",
        top: 0
      },
      imageUpload: {
        height: "100%",
        left: 0,
        outline: 0,
        position: "absolute",
        top: 0,
        width: "100%"
      },
      imageUploadActive: {
        zIndex: 1
      },
      imageUploadIconActive: {
        display: "block"
      },
      photosIcon: {
        height: "64px",
        margin: "0 auto",
        width: "64px"
      },
      root: {
        display: "grid",
        gridColumnGap: theme.spacing(2),
        gridRowGap: theme.spacing(2),
        gridTemplateColumns: "repeat(4, 1fr)",
        [theme.breakpoints.down("sm")]: {
          gridTemplateColumns: "repeat(3, 1fr)"
        },
        [theme.breakpoints.down("xs")]: {
          gridTemplateColumns: "repeat(2, 1fr)"
        }
      },
      rootDragActive: {
        opacity: 0.2
      },
      uploadButton: {
        display: "block"
      },
      uploadRoot: {
        fontSize: "20px",
        padding: "100px"
      }
    }),
    { name: "AnnouncementImage" }
  );

  const classes = useStyles(props);
  const intl = useIntl();
  const upload = React.useRef(null);
  const [imageToUpload, setImageToUpload] = React.useState(null);

  React.useEffect(() => {
    if (imageToUpload) {
      onImageUpload(imageToUpload[0]);
    }
  }, [imageToUpload]);

  const checkFile = e => {
    const file = e[0];
    if (
      file.type.includes("image/jpeg") ||
      file.type.includes("image/jpg") ||
      file.type.includes("image/png")
    ) {
      onChange({
        target: {
          name: "image",
          value: file
        }
      });
      setImageToUpload(e);
    } else {
      notify({
        status: "error",
        text: "File not supported"
      });
    }
  };

  return (
    <Card className={classes.card}>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Announcement Image",
          description: "section header"
        })}
        toolbar={
          <>
            <Button
              id="uploadImageBtn"
              onClick={() => upload.current.click()}
              disabled={loading}
              variant="text"
              color="primary"
              data-test="button-upload-image"
              className={classes.uploadButton}
            >
              {intl.formatMessage(commonMessages.uploadImage)}
            </Button>
            <input
              className={classes.fileField}
              id="imagefileUpload"
              onChange={event => checkFile(event.target.files)}
              type="file"
              ref={upload}
              accept="image/jpeg, image/jpg, image/png"
            />
          </>
        }
      />
      <div className={classes.imageGridContainer}>
        <CardContent>
          <Dropzone onDrop={checkFile} disableClick={true}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <Paper
                  className={classes.fileUploadField}
                  // onClick={() => upload.current.click()}
                >
                  <input
                    {...getInputProps()}
                    id="fileUpload"
                    accept="image/jpeg, image/jpg, image/png"
                    onChange={e =>
                      e.target.files.length > 0 && checkFile(e.target.files)
                    }
                    multiple={false}
                    ref={upload}
                    type="file"
                  />
                  {imageToUpload || data.image ? (
                    <div className={classes.imageContainer}>
                      <div
                        className={classNames(classes.imageOverlay, {
                          [classes.imageOverlayShow]: loading
                        })}
                      >
                        {loading ? (
                          <CircularProgress size={32} />
                        ) : (
                          <div className={classes.imageOverlayToolbar}>
                            <IconButton
                              id="deleteImageBtn"
                              color="primary"
                              onClick={() => {
                                upload.current.value = "";
                                setImageToUpload(null);
                                onChange({
                                  target: {
                                    name: "image",
                                    value: null
                                  }
                                });
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      <img
                        id="announcementImage"
                        className={classes.image}
                        src={
                          imageToUpload
                            ? URL.createObjectURL(imageToUpload[0])
                            : data.image
                        }
                      />
                    </div>
                  ) : (
                    <div className={classes.uploadRoot}>
                      <ImageIcon className={classes.photosIcon} />
                      <p className={classes.descriptionLabel}>
                        <FormattedMessage
                          defaultMessage="DROP IMAGE HERE TO UPLOAD"
                          description="description"
                        />
                      </p>
                    </div>
                  )}
                </Paper>
              </div>
            )}
          </Dropzone>
        </CardContent>
      </div>
    </Card>
  );
};
AnnouncementImage.displayName = "AnnouncementImage";
export default AnnouncementImage;
